<template lang="pug">
#other-office-facility-detail(v-cloak)
  .box
    .box-body.register-body
      .d-flex.flex-row
        .facility-image.m-4(v-if="image")
          b-img.facility-image(:src="image" :alt="_facilityImageAlt" fluid)
        .facility-basic-info.m-4(v-if="facility")
          .facility-name.mt-2.mb-2
            span {{ facility.name }}
          .facility-type.mb-1(v-if="_facilityCategory")
             span {{ _facilityCategory }}
          .facility-address.mb-1
            span {{ facility.address }}
          .facility-tel.mb-1
            span {{ facility.tel }}
          .facility-url.mb-1
            a.link(:href="facility.url" target="_blank")
              span.link-text(style="margin:0;") {{ facility.url }}
      .d-flex.flex-column
        CareLebelLabel(:value="_facilityCareLevels")
        FormLabel(key-name="生活保護" :value="_acceptsWelfare")
        MedicalNursingsLabel(:value="_facilityMedicalNursings")
        FormLabel(key-name="受け入れ条件の補足事項" :value="_acceptanceConditions")

    .box-footer.with-border.register-footer
      .buttons
        button.btn.cancel-btn(@click="gotoList" type="button") 戻る

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import Setting from "../../../../public/setting.json";
import { PcUrl } from "@/commons/constants/PcUrl";

import FacilitiesService, { CareLevels, FacilityCategory, FacilityGetModel, MedicalNursings } from "@/commons/services/Facilities";
import S3UrlService from "@/commons/services/S3Url";

import PageLoading from "@/pc/basics/PageLoading.vue";
import CareLebelLabel from "@/pc/components/base/CareLebelLabel.vue";
import FormLabel from "@/pc/components/forms/FormLabel.vue";
import MedicalNursingsLabel from "@/pc/components/base/MedicalNursingsLabel.vue";

/**
 * ### 他事業所連携 他施設詳細画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    CareLebelLabel,
    FormLabel,
    MedicalNursingsLabel
  }
})
export default class OtherOfficeFacilityDetail extends Vue {
  /** 施設サービス */
  @Inject("FacilitiesService") facilitiesService!: FacilitiesService;
  /** S3Urlサービス */
  @Inject("S3UrlService") s3UrlService!: S3UrlService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 施設 */
  facility: FacilityGetModel | null = null;
  /** 施設画像 */
  image: string = "";

  get _facilityImageAlt(): string {
    return this.facility ? this.facility.name : "";
  }

  get _facilityCategory(): string | null {
    if (this.facility && this.facility.facilityDetailInfo && this.facility.facilityDetailInfo.facilityCategory) {
      switch (this.facility.facilityDetailInfo.facilityCategory) {
        case FacilityCategory.KAIGO_YUURYOU:
          return "介護付有料老人ホーム";
        case FacilityCategory.JYUUTAKU_YUURYOU:
          return "住宅型有料老人ホーム  ";
        case FacilityCategory.SERVICE_JYUUTAKU:
          return "サービス付き高齢者向け住宅";
        case FacilityCategory.CONDOMINIUMS:
          return "シニア向け分譲マンション";
        case FacilityCategory.GROUP_HOME:
          return "グループホーム";
        case FacilityCategory.CARE_HOUSE:
          return "ケアハウス";
        case FacilityCategory.TOKUYO:
          return "特別養護老人ホーム（特養）";
        case FacilityCategory.KAIGO_HOKEN:
          return "介護老人保健施設（老健）";
        case FacilityCategory.MEDICAL_CENTER:
          return "介護医療院";
        case FacilityCategory.OTHER:
          return "その他";
        default:
          return "";
      }
    }

    return null;
  }

  get _facilityCareLevels(): CareLevels | null {
    if (this.facility && this.facility.facilityDetailAccept && this.facility.facilityDetailAccept.careLevels) {
      return this.facility.facilityDetailAccept.careLevels;
    }
    return null;
  }

  get _acceptsWelfare(): string {
    if (this.facility && this.facility.facilityDetailAccept) {
      if (this.facility.facilityDetailAccept.acceptsWelfare == true) {
        return "受け入れ可";
      } else if (this.facility.facilityDetailAccept.acceptsWelfare == false) {
        return "受け入れ不可";
      } else {
        return "";
      }
    } else {
      return "";
    }
  }

  get _facilityMedicalNursings(): MedicalNursings | null {
    if (this.facility && this.facility.facilityDetailAccept && this.facility.facilityDetailAccept.medicalNursings) {
      return this.facility.facilityDetailAccept.medicalNursings;
    }
    return null;
  }

  get _acceptanceConditions(): string {
    if (this.facility && this.facility.facilityDetailAccept && this.facility.facilityDetailAccept.acceptanceConditions) {
      return this.facility.facilityDetailAccept.acceptanceConditions;
    } else {
      return "";
    }
  }

  /**
   * ### vue lifecycle created
   */
  async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  private async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    // 取得先の施設IDをurlから抜き出し、施設情報を取得する
    const facilityId: number = Number(this.$route.params.id);
    this.facility = await this.getFacility(facilityId);
    this.image = await this.convertImageUrl(this.facility.imageUrl);

    this.isBusy = false;
    return;
  }

  /**
   * 施設取得
   */
  private async getFacility(facilityId: number): Promise<FacilityGetModel> {
    const response: FacilityGetModel = await this.facilitiesService.get(userStore, { targetFacilityId: facilityId });
    return response;
  }

  /**
   * DBに保存されているS3URLを公開状態にし、公開状態にした画像のURLを返却する
   */
  private async convertImageUrl(imageUrl: string): Promise<string> {
    // 画像のURLがない場合は空を返す
    if (!imageUrl) {
      return "";
    }

    // 画像URL取得
    const convertImageUrl = await this.s3UrlService.get(userStore, Setting.backets.imageExpireHour, imageUrl);

    return convertImageUrl;
  }

  gotoList(): void {
    this.$router.push(PcUrl.HomeForOtherOffice);
    return;
  }
}
</script>

<style lang="scss" scoped>
#other-office-facility-detail {
  max-width: 1020px;
  margin-left: auto;
  margin-right: auto;

  .facility-image {
    width: 435px;
    max-width: 435px;
    min-width: 435px;
  }

  .facility-basic-info {
    .facility-name {
      font-size: 20px;
      font-weight: bold;
    }
  }

  .cancel-btn {
    width: 120px;
  }
}
</style>
